import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import type { IEmailMarketingTemplateListItem } from "api/rpc/2024-04/facilityAdmin/marketing/emailMarketingTemplatesTypes";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import {
  isEmailBuilderBlankSystemTemplate,
  resolveEmailBuilderTemplateCardPreviewSrc,
} from "../emailBuilderCampaignEntry";
import "./emailBuilderTemplateLibrary.scss";

type TEmailBuilderTemplateLibraryCardProps = {
  template: IEmailMarketingTemplateListItem;
  disabled: boolean;
  onEdit: (templateId: number) => void;
  onRename: (template: IEmailMarketingTemplateListItem) => void;
  onDuplicate: (template: IEmailMarketingTemplateListItem) => void;
  onDelete: (template: IEmailMarketingTemplateListItem) => void;
};

export function EmailBuilderTemplateLibraryCard(props: TEmailBuilderTemplateLibraryCardProps) {
  const { t } = useTranslation();
  const previewSrc = resolveEmailBuilderTemplateCardPreviewSrc(props.template);
  const isBlankIllustration = isEmailBuilderBlankSystemTemplate(props.template);

  return (
    <article
      className="email-builder-template-picker-card email-builder-template-library-card"
      data-email-builder-template-id={props.template.id}
      data-email-builder-template-scope="facility"
    >
      <button
        type="button"
        className="email-builder-template-library-card__action"
        disabled={props.disabled}
        aria-label={t("Edit {{name}}", { name: props.template.name })}
        onClick={() => props.onEdit(props.template.id)}
      >
        <div
          className={
            isBlankIllustration
              ? "email-builder-template-picker-card__preview email-builder-template-picker-card__preview--blank"
              : "email-builder-template-picker-card__preview"
          }
          aria-hidden="true"
        >
          {previewSrc != null ? (
            <img
              className={
                isBlankIllustration
                  ? "email-builder-template-picker-card__image email-builder-template-picker-card__image--blank"
                  : "email-builder-template-picker-card__image"
              }
              src={previewSrc}
              alt=""
              loading="lazy"
            />
          ) : (
            <div className="email-builder-template-picker-card__placeholder">
              <span className="email-builder-template-picker-card__placeholder-label">
                {t("No preview")}
              </span>
            </div>
          )}
        </div>
        <div className="email-builder-template-picker-card__title" title={props.template.name}>
          {props.template.name}
        </div>
      </button>
      <div
        className="email-builder-template-library-card__menu"
        aria-label={t("Template actions for {{name}}", { name: props.template.name })}
      >
        <NavigationDropdownNew
          hideChevron
          showPlainTextLabel
          rightAlign
          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
          sections={[
            [
              {
                label: t("Edit"),
                icon: "pencil",
                onClick: () => props.onEdit(props.template.id),
              },
              {
                label: t("Rename"),
                icon: "text",
                onClick: () => props.onRename(props.template),
              },
              {
                label: t("Duplicate"),
                icon: "copy",
                onClick: () => props.onDuplicate(props.template),
              },
              {
                label: t("Delete"),
                icon: "trash",
                color: "red",
                onClick: () => props.onDelete(props.template),
              },
            ],
          ]}
        />
      </div>
    </article>
  );
}
